import React, { useEffect, useRef, useMemo } from 'react';

import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import LoyaltyLockIcon from '~/icons/components/LoyaltyLockIcon';
import { IconSizes } from '~/shared/constants/iconSizes';
import { LoyaltyTiers } from '~/shared/constants/loyaltyTiers';
import { productDetailPath } from '~/shared/constants/product';
import getCartPrice from '~/shared/utils/getCartPrice';
import useGridGender from '~/shared/utils/useGridGender';
import { Router } from '~/techstyle-shared/next-routes';
import { useLoyalty, useMembership } from '~/techstyle-shared/react-accounts';
import { useCart } from '~/techstyle-shared/react-cart';
import {
  Carousel as BaseCarousel,
  ProductContext,
  ProductCell as BaseProductCell,
  ProductImage,
  ProductName as BaseProductName,
  mobile,
  useBreakpoint,
  useId,
} from '~/techstyle-shared/react-components';
import {
  useProductListViewed,
  ProductListViewedProvider,
  trackProductClicked,
} from '~/techstyle-shared/react-products';
import {
  buildURL,
  getQueryParametersFromURL,
  useSession,
} from '~/techstyle-shared/redux-core';

import { ProductTag } from '../constants/products';
import getImageUrl from '../utils/getImageUrl';
import { isLockedToMember } from '../utils/loyalty';
import useSecondarySite from '../utils/useSecondarySite';

import ProductCellPriceDetails from './ProductCellPriceDetails';
import WishlistToggleButton from './WishlistToggleButton';

const breakpoints = [{ minWidth: 0, pictureWidth: 168 }];

const FullWidthCarousel = styled(BaseCarousel)`
  ${({ theme }) => theme.pdpStyles.upsellCarousel.fullWidthCarousel};

  & .flickity-prev-next-button,
  .flickity-prev-next-button:disabled {
    background-color: white;
    border: 1px solid ${({ theme }) => theme.colors.upsellCarouselBorder};
    box-shadow: 0px 8px 10px -5px ${({ theme }) => theme.colors.upsellCarouselBorder};
    color: ${({ theme }) => theme.colors.sublabelBlack};
    opacity: 1;
    width: 48px;
    height: 48px;

    ${mobile`
      display:none;
    `}

    &.previous {
      left: -22px;
      top: 130px;
    }

    &.next {
      right: -22px;
      top: 130px;
    }

    & .flickity-button-icon {
      width: 20px;
      height: 20px;
      left: 30%;
      top: 30%;
    }
  }

  & .flickity-prev-next-button:disabled {
    display: none;
  }
`;

const ProductCell = styled(BaseProductCell)`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  position: relative;
`;

const ProductLink = styled.a`
  display: block;
`;

const ProductImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ theme }) => theme.gridCellStyles.upsellCarousel.productImageWrapper}
`;

const ProductName = styled(BaseProductName)`
  text-align: left;
  display: block;
  font-size: 16px;

  ${({ theme }) => theme.gridCellStyles.upsellCarousel.productName}
`;

const cellStyle = css`
  margin-right: 24px;
  width: 168px;

  ${mobile`
    width: calc((100% - 9px)/2);
    margin-right: 9px;
  `}
`;

const progressBarStyle = css`
  background-color: ${({ theme }) => theme.colors.palette.white};
`;

const hiddenProgressBarStyle = css`
  visibility: hidden;
`;

const progressIndicatorStyle = css`
  background: ${({ theme }) =>
    theme.colors.carouselProgressIndicatorBackground};
  border-radius: 4px;
`;

const promoMessageStyles = css`
  margin-bottom: 4px;
  color: ${({ theme }) => theme.colors.linkText};
  line-height: 1.33;
  letter-spacing: ${-0.3 / 12}em;
  text-transform: uppercase;
  word-break: break-word;

  ${({ theme }) => theme.gridCellStyles.upsellCarousel.promoMessage}
`;

const promoPriceStyle = css`
  line-height: 1.33;
  letter-spacing: ${-0.3 / 14}em;

  ${({ theme }) => theme.gridCellStyles.upsellCarousel.promoPrice}
`;

const priceContentStyles = css`
  margin-bottom: 4px;
`;

const priceStyle = css`
  font-size: 14px;
  letter-spacing: ${-0.4 / 14}em;
  line-height: 1.14;
  font-weight: 700;

  ${({ theme }) => theme.gridCellStyles.priceStyles}
`;

const messageWrapperStyle = css`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.upsellCarouselMessage};
  line-height: 1.14;
  letter-spacing: ${-0.35 / 12}em;
`;

const messageStyle = css`
  color: ${({ theme }) => theme.colors.sublabelBlack};
`;

const empMessageProps = {
  messageStyle,
  messageWrapperStyle,
  'data-autotag': 'upsell_credit_price',
};

const ImageOverlay = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
`;

const toggleButtonStyle = css`
  padding: 6px;
`;

const StyledLoyaltyLockIcon = styled(LoyaltyLockIcon)`
  background: ${({ theme: { colors } }) => colors.loyaltyLockIcon};
  border-radius: 50%;
  padding: 3px;
`;

function ProductCellData({
  product,
  href,
  gridIndex,
  fplIds,
  isPlusSizeSelected,
  isVip,
  psrc,
}) {
  const productListViewedContext = useProductListViewed();
  const dispatch = useDispatch();
  const imageType = isPlusSizeSelected ? 'plus' : 'default';
  const imageUrl = getImageUrl({ product, imageType });
  const { isLoggedIn } = useSession();
  const gridGender = useGridGender();
  const { isVipCart } = useCart({ isLoginRequired: true }) || {};
  const secondarySite = useSecondarySite();

  const productInfo = useMemo(
    () => ({
      product,
      href: href.split(/[#?]/)[0],
      gridIndex,
      fplId: fplIds,
      productSource: psrc,
      payloadKey: psrc,
      currentGridKey: `carouselGridKey`,
      image_url: imageUrl,
    }),
    [fplIds, gridIndex, href, product, psrc, imageUrl]
  );

  const { tier } = useLoyalty();
  const isLocked = isLockedToMember(tier?.label, LoyaltyTiers.PLATINUM);
  const isPlatinumExclusive = product?.tagIdList?.includes(
    ProductTag.platinumExclusive
  );
  const showLockIcon = isLocked && isPlatinumExclusive;

  const handleClick = async (event) => {
    event.preventDefault();
    const { href } = event.currentTarget;

    const queryObject = {
      ...getQueryParametersFromURL(href),
      gridGender,
    };

    dispatch(
      trackProductClicked({
        ...productInfo,
        productUrl: href,
      })
    );

    if (!(event.metaKey || event.ctrlKey)) {
      const { href } = event.currentTarget;
      setTimeout(() => {
        Router.push(
          {
            pathname: `/${productDetailPath}/[identifier]`,
            query: queryObject,
          },
          href
        );
      }, 300);
    }
  };

  useEffect(() => {
    const node = productCellRef.current;
    if (node && productListViewedContext) {
      const { observe, unobserve } = productListViewedContext;
      observe(node, productInfo);
      return () => unobserve(node);
    }
  }, [productListViewedContext, productInfo]);

  const productPrice = getCartPrice({
    product,
    isVip,
    isVipCart,
    secondarySite,
  });

  const productCellRef = useRef();

  return (
    <ProductCell
      product={product}
      ref={productCellRef}
      data-cnstrc-item-id={product.masterProductId}
      data-cnstrc-item-name={product.label}
      data-cnstrc-item-price={productPrice}
    >
      <ProductLink href={href} aria-label={product.label} onClick={handleClick}>
        <ProductImageWrapper>
          <ProductImage
            alt={product.label}
            breakpoints={breakpoints}
            disableHover
            type={isPlusSizeSelected ? 'plus' : 'default'}
            style={{ opacity: showLockIcon ? 0.5 : 1 }}
          />
          {showLockIcon && (
            <StyledLoyaltyLockIcon
              width={IconSizes.STANDARD}
              height={IconSizes.STANDARD}
              color="white"
            />
          )}
        </ProductImageWrapper>
        <ProductName />
        <ProductCellPriceDetails
          empMessageProps={empMessageProps}
          priceStyle={priceStyle}
          promoPriceStyle={promoPriceStyle}
          promoMessageStyles={promoMessageStyles}
          priceContentStyles={priceContentStyles}
        />
      </ProductLink>
      {isLoggedIn ? (
        <ImageOverlay>
          <WishlistToggleButton toggleButtonStyle={toggleButtonStyle} />
        </ImageOverlay>
      ) : null}
    </ProductCell>
  );
}

ProductCellData.propTypes = {
  fplIds: PropTypes.array,
  gridIndex: PropTypes.number,
  href: PropTypes.string,
  isPlusSizeSelected: PropTypes.bool,
  isVip: PropTypes.bool,
  product: PropTypes.object,
  psrc: PropTypes.string,
};

const UpsellContent = ({
  products,
  fplIds = [],
  psrc = '',
  isPlusSizeSelected = false,
}) => {
  const gridGender = useGridGender();
  const { isVip } = useMembership();

  return (
    products &&
    products.map((product, index) => {
      const href = buildURL(`/${productDetailPath}/${product.permalink}`, {
        psrc,
        ...(gridGender && { gridGender }),
      });
      return (
        <ProductContext
          key={`bundle-item-${product.masterProductId}-${index}`}
          product={product}
          productIndex={index}
        >
          <ProductCellData
            href={href}
            psrc={psrc}
            product={product}
            gridIndex={index}
            fplIds={fplIds}
            isPlusSizeSelected={isPlusSizeSelected}
            key={`bundle-item-${product.masterProductId}-${index}`}
            isVip={isVip}
          />
        </ProductContext>
      );
    })
  );
};

export const UpsellList = ({ products, psrc, isPlusSizeSelected = false }) => {
  const segmentId = useId('upsellList');

  return (
    <ProductListViewedProvider id={segmentId}>
      <UpsellContent
        products={products}
        psrc={psrc}
        isPlusSizeSelected={isPlusSizeSelected}
      />
    </ProductListViewedProvider>
  );
};
UpsellList.propTypes = {
  isPlusSizeSelected: PropTypes.bool,
  products: PropTypes.array,
  psrc: PropTypes.string,
};

function UpsellCarousel({
  products,
  psrc,
  fplIds,
  isPlusSizeSelected = false,
  groupCellsDesktop = 5,
  groupCellsMobile = 2,
  minCountPagination = 5,
  showProgressBarStyle = true,
  cellAlign = 'left',
  autoPlay = true,
  ...rest
}) {
  const hasPagination = products.length > minCountPagination;
  const { isMobile } = useBreakpoint();
  const carouselId = useId('upsellCarousel');

  return (
    <ProductListViewedProvider id={carouselId}>
      <FullWidthCarousel
        cellAlign={cellAlign}
        cellStyle={cellStyle}
        enableProgressBar={!!isMobile}
        groupCells={isMobile ? groupCellsMobile : groupCellsDesktop}
        pageDots={false}
        prevNextButtons={hasPagination}
        wrapAround={false}
        draggable={!!isMobile}
        freeScroll={false}
        progressBarHeight={isMobile ? 8 : 0}
        progressBarStyle={
          products.length > 2 && isMobile && showProgressBarStyle
            ? progressBarStyle
            : hiddenProgressBarStyle
        }
        progressIndicatorStyle={progressIndicatorStyle}
        autoPlay={autoPlay}
        {...rest}
      >
        {UpsellContent({ products, psrc, fplIds, isPlusSizeSelected })}
      </FullWidthCarousel>
    </ProductListViewedProvider>
  );
}

UpsellCarousel.propTypes = {
  autoPlay: PropTypes.bool,
  cellAlign: PropTypes.string,
  fplIds: PropTypes.array,
  groupCellsDesktop: PropTypes.number,
  groupCellsMobile: PropTypes.number,
  isPlusSizeSelected: PropTypes.bool,
  minCountPagination: PropTypes.number,
  products: PropTypes.array,
  psrc: PropTypes.string,
  showProgressBarStyle: PropTypes.bool,
};

export default UpsellCarousel;
